import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiInstance } from "@/utils/api";

const api = getApiInstance();

export type SessionInvoice = {
  id: number;
  client_name: string;
  phone?: string;
  total_price: number;
  cash_amount: number;
  card_amount: number;
  date: string;
  time: string;
};

export type Session = {
  id: number;
  created_at: string;
  ended_at?: string | null;
  invoices?: SessionInvoice[];
  total_sales?: number;
  cash_total?: number;
  card_total?: number;
  is_active?: boolean;
};

export const useStartSession = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await api.post("/user/sessions/", {});
      return response.data;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["current-session"] });
      client.invalidateQueries({ queryKey: ["session-invoices"] });
    },
  });
};

export const useCloseSession = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: number) => {
      await api.post(`/user/sessions/${sessionId}/close_session/`);

      client.invalidateQueries({ queryKey: ["current-session"] });
      client.invalidateQueries({ queryKey: ["session-invoices"] });
    },
    onError: () => {
      // Handle error silently
    },
  });
};

export const useCurrentSession = () => {
  return useQuery<Session | null>({
    queryKey: ["current-session"],
    queryFn: async () => {
      const res = await api.get("/user/sessions/");
      const data = res.data;

      if (!data) {
        return null;
      }

      const collectSessions = async (resData: any): Promise<any[]> => {
        let arr: any[] = [];
        if (Array.isArray(resData)) return resData;
        if (resData.results) arr = resData.results;
        else if (resData.id) arr = [resData];

        if (!arr.some((s) => !s.ended_at) && resData.next) {
          try {
            const nextRes = await api.get(resData.next);
            const nextArr = await collectSessions(nextRes.data);
            return [...arr, ...nextArr];
          } catch {
            return arr;
          }
        }
        return arr;
      };

      const sessions = await collectSessions(data);

      const activeSession = sessions.find((s: Session) => !s.ended_at);

      if (activeSession) {
        return { ...activeSession, is_active: true } as any;
      }

      return null;
    },
    // تقليل الفلاش: استخدم أزمنة صلاحية مناسبة وتقليل إعادة الجلب
    select: (data) => data,
    staleTime: 60 * 1000,
    gcTime: 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchInterval: 30 * 1000,
  });
};

type SessionInvoicesResponse = {
  invoices: any[];
  next: string | null;
  previous: string | null;
  count: number;
  total_sales: number;
  cash_total: number;
  card_total: number;
  session_id: number | null;
};

export const useSessionInvoices = () => {
  const { data: currentSession } = useCurrentSession();
  const sessionId = (currentSession as Session | null | undefined)?.id ?? null;
  const sessionEnded = Boolean(
    (currentSession as Session | null | undefined)?.ended_at
  );

  return useQuery<SessionInvoicesResponse>({
    queryKey: ["session-invoices", sessionId],
    queryFn: async () => {
      // شرط صارم: يجب وجود جلسة نشطة (بدون ended_at)
      if (!sessionId) {
        return {
          invoices: [],
          next: null,
          previous: null,
          count: 0,
          total_sales: 0,
          cash_total: 0,
          card_total: 0,
          session_id: null,
        };
      }

      if (sessionEnded) {
        return {
          invoices: [],
          next: null,
          previous: null,
          count: 0,
          total_sales: 0,
          cash_total: 0,
          card_total: 0,
          session_id: sessionId,
        };
      }

      try {
        // جمع كل صفحات الفواتير الخاصة بالجلسة النشطة
        const firstRes = await api.get(`/store/invoices/?session=${sessionId}`);
        let allInvoices: any[] = (firstRes.data?.results || []) as any[];

        // تتبُّع الصفحات التالية باستخدام خاصية next من الباك إند
        let nextUrl: string | null = firstRes.data?.next || null;
        while (nextUrl) {
          try {
            const nextRes = await api.get(nextUrl);
            const pageResults: any[] = nextRes.data?.results || [];
            allInvoices = allInvoices.concat(pageResults);
            nextUrl = nextRes.data?.next || null;
          } catch {
            // في حال فشل صفحة تالية، نتوقف ونُكمل بما لدينا
            break;
          }
        }

        const totals = allInvoices.reduce(
          (acc: any, invoice: any) => {
            const total = (invoice.cash || 0) + (invoice.visa || 0);
            acc.total_sales += total;
            acc.cash_total += invoice.cash || 0;
            acc.card_total += invoice.visa || 0;
            return acc;
          },
          { total_sales: 0, cash_total: 0, card_total: 0 }
        );

        return {
          invoices: allInvoices,
          ...totals,
          session_id: sessionId,
        };
      } catch (error) {
        return {
          invoices: [],
          next: null,
          previous: null,
          count: 0,
          total_sales: 0,
          cash_total: 0,
          card_total: 0,
          session_id: sessionId,
        };
      }
    },
    // شرط صارم: فقط الجلسة النشطة (بدون ended_at)
    enabled: Boolean(sessionId && !sessionEnded),
    // حافظ على البيانات السابقة أثناء الجلب لتجنّب ارتجاج الواجهة
    select: (data) => data,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    refetchInterval: 60 * 1000,
  });
};
